import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isDev } from '@app/utils';
import { useAnalytics, useBreakpoint } from '@app/hooks';

import {
  LOCALHOST_CONTEXT_PREFIX,
  PAGE_BREAKPOINTS,
  STATUS
} from '@app/constants/commonConstants';
import { getPartName } from '@app/utils/commonUtils';
import cx from 'classnames';
import { FBT_LIST_NAME } from '@app/constants/analyticsConstants';
import FbtCard from '../FbtCard';
import styles from '../FrequentlyBoughtTogether.module.scss';
import { PCC_HEADLESS_PDP_FLAG } from '@app/constants/featureFlags';
import { convertFormattedNumberToUnformatted } from '@app/components/pages/checkout/utils';

const FbtItems = ({ getFitmentResult, model, handleCheckBox }) => {
  const { fireFbtImpressionEvent, fireFbtProductClickEvent } = useAnalytics();
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const fbtPriceAndAvailability = useSelector(
    s => s.products?.fbtPriceAndAvailability
  );
  const allfbtItems = useSelector(s => s.products?.fbtItems?.products);

  const fbtPriceAndAvailabilityItems = useSelector(
    s => s.products?.fbtPriceAndAvailability
  );
  const store = useSelector(s => s.store) ?? '';

  const getOtherStores = store.otherStores?.dealerStores.map(
    store => store.name
  );

  const fbtPriceAndAvailabilityItemsArray = fbtPriceAndAvailabilityItems
    ? Object.entries(fbtPriceAndAvailabilityItems).map(([key, value]) => value)
    : [];

  const fbtCompose = fbtPriceAndAvailabilityItemsArray.map(
    AvailabilityItem => ({
      ...AvailabilityItem,
      ...allfbtItems.find(
        item => item.partNumber === AvailabilityItem.partNumber
      )
    })
  );

  const topThreefbtItems = allfbtItems?.slice(0, 3);
  const fbtItems = isLG ? allfbtItems : topThreefbtItems;

  const getMarketingPriceStatus = useSelector(
    s => s.products?.getMarketingPriceStatus
  );
  const esiteURL = useSelector(s => s.common.esiteURL);
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const isPDPHeadlessFeatureFlagEnable =
    useSelector(s => s.featureFlag?.[PCC_HEADLESS_PDP_FLAG]) ?? false;

  //Note: As we not getting PDP redirect url from FBT Response
  const redirectUrl = isDev()
    ? `${LOCALHOST_CONTEXT_PREFIX}${esiteURL}`
    : esiteURL;

  const getProductDetails = useCallback(
    part => {
      return part.map((part, index) => ({
        availability: part?.availability,
        brand: part.manufacturer || 'Cat',
        businessUnit: 'PCC',
        category: part.categoryPath?.split('/'),
        checkFitment: getFitmentResult(part.partNumber),
        list: FBT_LIST_NAME,
        itemCatEntryId: part.itemCatEntryId,
        name: getPartName(part.partNumber, part.name),
        partNumber: part.partNumber,
        locationId: getOtherStores?.join('|'),
        shortDescription: part.shortDescription,
        price: convertFormattedNumberToUnformatted(
          part.unformattedUnitPrice || ''
        ),
        discountedAmount: convertFormattedNumberToUnformatted(
          part?.discountedAmount || ''
        ),
        productWeight: part.weight,
        position: index + 1,
        quantity: part.quantity,
        relatedModel: model,
        sourcingDetails: part.sourcingDetails
      }));
    },
    [fbtPriceAndAvailabilityItemsArray, store.otherStores?.dealerStores, model]
  );

  useEffect(() => {
    getMarketingPriceStatus === STATUS.RESOLVED &&
      fbtPriceAndAvailability &&
      fireFbtImpressionEvent(getProductDetails(fbtCompose), FBT_LIST_NAME);
  }, [
    fbtCompose,
    fbtPriceAndAvailability,
    fireFbtImpressionEvent,
    getMarketingPriceStatus,
    getProductDetails,
    model
  ]);

  return fbtItems?.map((item, index) => {
    const cardItem =
      !isCatCorp &&
      fbtPriceAndAvailability !== undefined &&
      fbtPriceAndAvailability[item.partNumber];
    const urlBasedonFeatureFlag = isPDPHeadlessFeatureFlagEnable
      ? `${esiteURL}/product/${item.partNumber}`
      : `${redirectUrl}/${item.partNumber}`;

    return (
      <div
        className={cx('border-0 ms-md-3 ms-0 mb-3 pt-3', styles['itemCard'])}
        key={cardItem.urlKeyword}
      >
        {(cardItem.loadingStatus === STATUS.RESOLVED ||
          (cardItem.loadingStatus === STATUS.PENDING && cardItem?.unitPrice) ||
          isCatCorp) && (
          <FbtCard
            partName={
              <a
                href={urlBasedonFeatureFlag}
                className="u-color--DarkGray"
                onClick={() =>
                  fireFbtProductClickEvent({
                    products: [
                      {
                        name: `${item?.partNumber}: ${item?.name}`,
                        partNumber: item.partNumber,
                        price: cardItem.unformattedTotalPrice,
                        position:
                          cardItem.position === undefined
                            ? index + 2
                            : cardItem?.position
                      }
                    ],
                    list: FBT_LIST_NAME,
                    dealerStore: getOtherStores?.join('|')
                  })
                }
              >
                {`${item.partNumber}: ${item.name}`}
              </a>
            }
            imageData={{ url: item.imageURL, title: item.partNumber }}
            getFitmentResult={getFitmentResult}
            price={cardItem.enableAddToCart && cardItem.totalPrice}
            partNumber={cardItem.partNumber}
            unformattedUnitPrice={cardItem.unformattedUnitPrice}
            handleCheckBox={handleCheckBox}
            loadingStatus={cardItem.loadingStatus}
          />
        )}
        {(cardItem.loadingStatus === STATUS.REJECTED ||
          fbtPriceAndAvailability === undefined) && (
          <FbtCard
            partName={
              <a
                href={urlBasedonFeatureFlag}
                className="u-color--DarkGray"
                onClick={() =>
                  fireFbtProductClickEvent({
                    products: [
                      {
                        name: `${item?.partNumber}: ${item?.name}`,
                        partNumber: item.partNumber,
                        price: cardItem.unformattedTotalPrice,
                        position:
                          cardItem.position === undefined
                            ? index + 2
                            : cardItem?.position
                      }
                    ],
                    list: FBT_LIST_NAME,
                    dealerStore: getOtherStores?.join('|')
                  })
                }
              >
                {`${item.partNumber}: ${item.name}`}
              </a>
            }
            imageData={{ url: item.imageURL, title: item.partNumber }}
            getFitmentResult={getFitmentResult}
          />
        )}
      </div>
    );
  });
};

FbtItems.propTypes = {
  getFitmentResult: PropTypes.func,
  model: PropTypes.string,
  handleCheckBox: PropTypes.func
};

export default FbtItems;
